import React, { useCallback, useState } from 'react';

import { InlineAlert } from '@rbilabs/components-library/build/components/inline-alert';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import CreditCardFormInputs from 'components/credit-card-form-inputs';
import PaymentMethodSelect, { HoverTheme } from 'components/payment-method-select';
import { usePrepaidsPaymentMethods } from 'hooks/prepaid/use-prepaids-payment-methods';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { SupportedCardType, defaultSupportedCardTypes } from 'state/launchdarkly/variations';
import { usePaymentContext } from 'state/payment';
import { CardType } from 'state/payment/types';
import { useUIContext } from 'state/ui';
import { initialErrorState, onCCFormChange } from 'utils/payment';
import { mapPaymentMethodCardTypes } from 'utils/payment/map-payment-method-card-type';

import { RELOAD_EXPLANATION_TRANSLATION_ID } from './constants';
import { InstructionText, LoadContainer, ReloadWrapper } from './styled';
import { IFullPrepaidMethodReloadProps } from './types';

const PrepaidMethodReload: React.FC<IFullPrepaidMethodReloadProps> = ({
  isDelivery,
  reloadAmount,
  reloadPaymentValues,
  setReloadPaymentValues,
}) => {
  const [errors, setErrors] = useState(initialErrorState);
  const { formatMessage } = useIntl();
  const { formatCurrencyForUser } = useUIContext();
  const { setPrepaidReloadPaymentMethodId } = usePaymentContext();
  const { nonSelectedReloadPaymentMethods, reloadPaymentMethods, selectedReloadPaymentMethod } =
    usePrepaidsPaymentMethods();
  const navigate = useNavigate();

  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];

  const enableSaveCard = !useFlag(LaunchDarklyFlag.HIDE_SAVE_CARD);

  const formattedReloadAmount = formatCurrencyForUser(reloadAmount);

  const onChange = useCallback(
    (name: string, value: string) => {
      const { state, formErrors } = onCCFormChange(
        name,
        value,
        reloadPaymentValues,
        errors,
        formatMessage,
        supportedCardTypes
      );
      setErrors(prevState => ({ ...prevState, ...formErrors }));
      setReloadPaymentValues(prevState => ({ ...prevState, ...state }));
    },
    [reloadPaymentValues, errors, formatMessage, setReloadPaymentValues, supportedCardTypes]
  );

  const reloadExplanationText = formatMessage(
    { id: RELOAD_EXPLANATION_TRANSLATION_ID },
    { reloadAmount: formattedReloadAmount }
  );
  const youWillBeChargedText = formatMessage(
    { id: 'youWillBeCharged' },
    { reloadAmount: formattedReloadAmount }
  );
  const reloadExplanationAndChargedAmountText = `${reloadExplanationText} ${youWillBeChargedText}`;

  return (
    <ReloadWrapper>
      <InlineAlert message={reloadExplanationAndChargedAmountText} />
      <LoadContainer>
        <InstructionText id="payment-method-loads-with">
          {formatMessage({ id: reloadPaymentMethods.length ? 'loadWith' : 'addCreditCard' })}:
        </InstructionText>
        {reloadPaymentMethods.length ? (
          <PaymentMethodSelect
            onOptionClick={setPrepaidReloadPaymentMethodId}
            nonSelectedMethods={nonSelectedReloadPaymentMethods}
            selectedMethod={selectedReloadPaymentMethod}
            hoverTheme={HoverTheme.LIGHT}
            onNavigateClick={(e, route) => navigate(route)}
          />
        ) : (
          <CreditCardFormInputs
            ariaLabel="credit-card-form-inputs"
            isDelivery={isDelivery}
            onChange={onChange}
            paymentValues={reloadPaymentValues}
            errors={errors}
            withBorder={false}
            showSaveCard={enableSaveCard}
          />
        )}
      </LoadContainer>
    </ReloadWrapper>
  );
};

export default PrepaidMethodReload;
